import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { refreshFolderLists } from '../../../lib/folderListRefresh';
import { queryKeys } from '../../../lib/queryClient';
import { apiService } from '../../../services/api.service';
import { updateArtifact } from '../../../services/api/artifacts.api';
import { unwrapApiResponse } from '../../../services/api/http-client.types';
import { safeAnalytics } from '../../../utils/analytics';
import { linkEditPatch, type LinkFieldValues } from './linkPrefill';

/**
 * Save an edited link: only what changed (content `{ url }`, the title, and
 * `metadata.favicon`, null for the Bower icon), then refresh the folder lists
 * so the row shows it. Resolves false, sending nothing, when nothing changed.
 */
export function useEditLink(linkId: string) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  return useMutation({
    retry: false,
    mutationFn: async ({
      before,
      after,
    }: {
      readonly before: LinkFieldValues;
      readonly after: LinkFieldValues;
    }) => {
      const { patch, changed } = linkEditPatch(before, after);
      if (changed.length === 0) return changed;
      unwrapApiResponse(await updateArtifact(apiService, linkId, patch), 'Could not save the link');
      return changed;
    },
    onSuccess: (changed) => {
      if (changed.length === 0) return;
      safeAnalytics.track(ANALYTICS_EVENTS.LINK_EDITED, { changed });
      refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.artifacts.detail(linkId) });
      void queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
    },
  });
}
